import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface SidebarItemRowProps {
  label: string;
  showTooltip: boolean;
  className?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: (ref: React.RefCallback<any>) => React.ReactNode;
}

/**
 * Row wrapper that renders a portal-based tooltip next to the icon when the
 * sidebar is collapsed. Portal escapes the inner overflow:auto scroll
 * container that would otherwise clip absolutely-positioned tooltips.
 */
export const SidebarItemRow: React.FC<SidebarItemRowProps> = ({ label, showTooltip, className, children }) => {
  const elRef = useRef<HTMLElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const setRef = (node: HTMLElement | null) => {
    elRef.current = node;
  };

  const open = () => {
    if (!showTooltip || !elRef.current) return;
    const r = elRef.current.getBoundingClientRect();
    setCoords({ top: r.top + r.height / 2, left: r.right + 8 });
  };
  const close = () => setCoords(null);

  return (
    <div
      className={className}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
    >
      {children(setRef)}
      {showTooltip && coords &&
        createPortal(
          <span
            role="tooltip"
            style={{ top: coords.top, left: coords.left, transform: 'translateY(-50%)' }}
            className="fixed z-[100] pointer-events-none whitespace-nowrap rounded-md bg-popover px-2.5 py-1.5 text-xs font-medium text-popover-foreground shadow-md border border-border animate-in fade-in-0 zoom-in-95 duration-100"
          >
            {label}
          </span>,
          document.body
        )}
    </div>
  );
};
